import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PageShell from '../components/Layout/PageShell'
import { formatUserDisplayName } from '../utils/userDisplayName'
import PortalHeader from '../components/Layout/PortalHeader'
import { PORTAL_ROLES } from '../components/Sidebar/portalNav'
import {
  DIGEST_FILTERS,
  DIGEST_TYPES,
  classifyDigestItem,
  digestGreeting,
  digestDisplaySummary,
  formatDigestTime,
  formatDigestDate,
  groupDigestItems,
  langBadge,
  markDigestRead,
  syncLegacyDigestReads,
  fetchDigestFeed,
} from '../utils/digestHelpers'
import toast from 'react-hot-toast'
import { RsIcon, IconLabel } from '../components/icons'

export default function Digest({ embedded = false }) {
  const { user, role, isAdmin, isPaid } = useAuth()
  const navigate = useNavigate()
  const isPortal = PORTAL_ROLES.includes(role) || embedded || isAdmin
  const [items, setItems] = useState([])
  const [members, setMembers] = useState([])
  const [memberMeta, setMemberMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [membersLoading, setMembersLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setLoadError(null)
    fetchDigestFeed(50)
      .then((rows) => setItems(rows))
      .catch(() => {
        setItems([])
        setLoadError('Unable to load digest feed. Please check that the backend is running and refresh.')
        toast.error('Failed to load digest feed')
      })
      .finally(() => setLoading(false))
  }, [])

  const loadMembers = useCallback(() => {
    setMembersLoading(true)
    axios
      .get('/api/users/directory')
      .then((res) => {
        setMembers(res.data.members || [])
        setMemberMeta({
          total: res.data.total || 0,
          isPaid: res.data.isPaid,
        })
      })
      .catch(() => {
        setMembers([])
        setMemberMeta(null)
      })
      .finally(() => setMembersLoading(false))
  }, [])

  useEffect(() => {
    load()
    if (isPortal) loadMembers()
    syncLegacyDigestReads().catch(() => {})
  }, [load, loadMembers, isPortal])

  useEffect(() => {
    if (!loading && !membersLoading && isPortal && items.length === 0 && members.length > 0 && filter === 'all') {
      setFilter('members')
    }
  }, [loading, membersLoading, isPortal, items.length, members.length, filter])

  useEffect(() => {
    const sync = () => load()
    window.addEventListener('rs-digest-read-changed', sync)
    window.addEventListener('rs-notifications-changed', sync)
    return () => {
      window.removeEventListener('rs-digest-read-changed', sync)
      window.removeEventListener('rs-notifications-changed', sync)
    }
  }, [load])

  const enriched = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        type: classifyDigestItem(item),
        unread: item.unread !== false && !item.isRead,
      })),
    [items]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return enriched.filter((item) => {
      if (filter !== 'all' && item.type !== filter) return false
      if (!q) return true
      const hay = `${item.title} ${digestDisplaySummary(item)} ${item.source_name || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [enriched, filter, search])

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase()
    return members.filter((member) => {
      if (!q) return true
      const hay = `${member.name} ${member.company || ''} ${member.role || ''} ${member.position || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [members, search])

  const groups = useMemo(() => groupDigestItems(filtered), [filtered])
  const { greeting, briefLabel } = digestGreeting(user?.real_name || user?.system_name)
  const todayLabel = formatDigestDate(new Date())

  const handleRead = (item) => {
    markDigestRead(item.id).then(() => {
      setItems((prev) => prev.map((row) => (
        row.id === item.id ? { ...row, isRead: true, unread: false } : row
      )))
    })
  }

  if (!isPortal) {
    return (
      <PageShell embedded={embedded}>
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold text-navy mb-4">Industry Digest</h1>
          <p className="text-gray-600 mb-6">Curated intelligence briefing from configured RSS sources.</p>
          {loading && <p>Loading…</p>}
          {!loading && !filtered.length && <p className="text-gray-500">No digest items yet.</p>}
          <div className="space-y-4">
            {filtered.map((item) => (
              <article key={item.id} className="card p-4">
                <h2 className="font-semibold text-navy">{item.title}</h2>
                <p className="text-sm text-gray-600 mt-2">{digestDisplaySummary(item)}</p>
                {item.original_url && (
                  <a href={item.original_url} target="_blank" rel="noreferrer" className="text-primary-600 text-sm mt-2 inline-block">
                    Read original →
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell embedded={embedded}>
      <PortalHeader
        title="Intelligence Digest"
        searchPlaceholder="Search digest..."
        searchValue={search}
        onSearchChange={setSearch}
      />

      <div className="portal-digest-hero">
        <div>
          <h2>{greeting}</h2>
          <p>Here&apos;s your curated intelligence briefing for today</p>
        </div>
        <div className="portal-digest-time-badge">
          <IconLabel name="digest" size="sm">
            <span>{briefLabel} · {todayLabel}</span>
          </IconLabel>
        </div>
      </div>

      <div className="portal-digest-filters">
        {DIGEST_FILTERS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`portal-filter-pill${filter === tab.id ? ' active' : ''}`}
            onClick={() => setFilter(tab.id)}
          >
            {tab.icon ? (
              <IconLabel name={tab.icon} size="xs">{tab.label}</IconLabel>
            ) : (
              tab.label
            )}
          </button>
        ))}
      </div>

      {loading && filter !== 'members' && (
        <p className="portal-digest-empty">Loading intelligence briefing…</p>
      )}

      {membersLoading && filter === 'members' && (
        <p className="portal-digest-empty">Loading platform members…</p>
      )}

      {!loading && filter !== 'members' && loadError && (
        <div className="portal-digest-empty">
          <p>{loadError}</p>
          <button type="button" className="portal-digest-inline-link" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {!loading && filter !== 'members' && !loadError && !filtered.length && (
        <p className="portal-digest-empty">
          No industry digest items match your filters.
          {items.length === 0 && isPortal && members.length > 0 && (
            <> Switch to <button type="button" className="portal-digest-inline-link" onClick={() => setFilter('members')}>Members</button> to browse {members.length} registered peers.</>
          )}
          {items.length === 0 && !isPortal && (
            <>
              {' '}Admin can configure sources under <Link to="/admin/settings?tab=digest">Digest Config</Link>.
            </>
          )}
        </p>
      )}

      {filter === 'members' && isPortal && !membersLoading && (
        <>
          <div className="portal-digest-members-banner">
            <p>
              {memberMeta?.total ?? filteredMembers.length} active members on RotationStreet.
              {isPaid
                ? ' You can browse everyone; new chat quotas apply when starting conversations.'
                : ' You can view all members; upgrade to message GP/LP/Startup contacts.'}
            </p>
          </div>
          {!filteredMembers.length && (
            <p className="portal-digest-empty">No members match your search.</p>
          )}
          <div className="portal-digest-members">
            {filteredMembers.map((member) => (
              <article key={member.id} className="portal-digest-member-card">
                <div className="portal-digest-member-head">
                  <div>
                    <h3 className="portal-digest-member-name">{formatUserDisplayName(member)}</h3>
                    <p className="portal-digest-member-meta">{member.role || '—'}</p>
                  </div>
                  <span className={`portal-digest-member-role role-${(member.role || 'GP').toLowerCase()}`}>
                    {member.role}
                  </span>
                </div>
                <div className="portal-digest-member-footer">
                  <span className={`portal-digest-member-status${member.canChat ? ' chat' : ''}`}>
                    {member.canChat ? 'Can message' : 'View only'}
                  </span>
                  <button
                    type="button"
                    className="portal-digest-link"
                    onClick={() => navigate(`/chat?contact=${member.id}`)}
                  >
                    {member.canChat ? 'Open chat →' : 'View profile →'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {filter !== 'members' && !loading && !loadError && groups.map((group) => (
        <section key={group.key}>
          <div className="portal-digest-period">
            {group.periodLabel} — {group.dateLabel}
          </div>
          <div className="portal-digest-list">
            {group.items.map((item) => {
              const meta = DIGEST_TYPES[item.type] || DIGEST_TYPES.market
              const lang = langBadge(item.original_lang)
              return (
                <article
                  key={item.id}
                  className={`portal-digest-card${item.unread ? ' unread' : ''}`}
                  onClick={() => handleRead(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRead(item)
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {item.unread && <span className="portal-digest-unread-dot" aria-hidden />}
                  <div className={`portal-digest-type-icon dtype-${meta.iconClass}`}>
                    <RsIcon name={meta.icon} size="md" />
                  </div>
                  <div className="portal-digest-body">
                    <div className="portal-digest-meta">
                      <span className={`portal-digest-type-label dt-${meta.labelClass}`}>{meta.label}</span>
                      <span className="portal-digest-source">{item.source_name || 'RotationStreet'}</span>
                      <span className={`portal-digest-lang lang-${lang.className}`}>{lang.label}</span>
                    </div>
                    <h3 className="portal-digest-title">{item.title}</h3>
                    {digestDisplaySummary(item) && (
                      <p className="portal-digest-snippet">{digestDisplaySummary(item)}</p>
                    )}
                    <div className="portal-digest-footer">
                      <span className="portal-digest-time">
                        {formatDigestTime(item.published_at || item.fetched_at)}
                      </span>
                      {item.original_url && (
                        <a
                          href={item.original_url}
                          target="_blank"
                          rel="noreferrer"
                          className="portal-digest-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Read original →
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        </section>
      ))}
    </PageShell>
  )
}
